import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Database,
  Cpu,
  Workflow,
  Terminal,
  ShieldCheck,
  Layers,
  GitBranch,
  FileCode2,
  MessageSquare,
  FolderKanban,
  CheckCircle2,
  ChevronDown,
  Download,
  ArrowRight,
  Sparkles,
  Users,
  Code2,
  Search,
  Boxes,
  HelpCircle,
  Mail,
  X,
  Zap,
  Globe,
  Check,
  Bot,
  Mic,
  Monitor,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

// Database Logos Marquee Array
const DB_LOGOS = [
  { name: 'Snowflake', icon: '❄️' },
  { name: 'Databricks', icon: '🧱' },
  { name: 'PostgreSQL', icon: '🐘' },
  { name: 'MySQL', icon: '🐬' },
  { name: 'SQL Server', icon: '🗄️' },
  { name: 'Oracle', icon: '⚙️' },
  { name: 'DuckDB', icon: '🦆' },
  { name: 'SQLite', icon: '📦' },
  { name: 'MariaDB', icon: '🦭' },
  { name: 'Vertica', icon: '⚡' },
  { name: 'SAP HANA', icon: '💼' }
];

// Feature Grid Data (16 Unique Features)
const FEATURE_GRID = [
  {
    id: 'schema-aware-ai',
    title: 'Schema-Aware AI',
    description:
      'Generate SQL and ETL workflows using live database metadata. Candor uses active database schemas to provide context-aware assistance for SQL transformation, ETL generation, and pipeline improvements.',
    icon: Database,
    color: 'from-blue-600 to-cyan-600',
    accentBorder: 'border-slate-200 hover:border-blue-500',
    badge: 'Core Engine',
    codeSnippet: `-- Schema-Aware Generated SQL (Snowflake Target)
SELECT 
  o.order_id,
  c.customer_name,
  c.email,
  SUM(o.amount) AS total_spent,
  COUNT(o.order_id) AS order_count
FROM snowflake_prod.public.orders o
JOIN snowflake_prod.public.customers c ON o.customer_id = c.customer_id
WHERE o.order_date >= CURRENT_DATE() - INTERVAL '30 days'
GROUP BY 1, 2, 3
HAVING SUM(o.amount) > 1000;`,
    demoHeadline: 'Context-Aware SQL Transformation Engine',
    demoBullets: [
      'Introspects tables, column types, and foreign keys in real-time.',
      'Eliminates hallucinated table names and missing join conditions.',
      'Auto-formats target dialect SQL (Snowflake, Postgres, T-SQL, Oracle).'
    ]
  },
  {
    id: 'ultra-ai-debugger',
    title: 'Ultra AI Debugger',
    description:
      'Analyze repositories, troubleshoot SQL and Python code, generate documentation, and receive guided fixes through Candor\'s AI-powered debugging assistant.',
    icon: Cpu,
    color: 'from-purple-600 to-indigo-600',
    accentBorder: 'border-slate-200 hover:border-purple-500',
    badge: 'Repository-Aware',
    codeSnippet: `# Ultra AI Debugger: Fixing Null Pointer in Spark Pipeline
def process_customer_metrics(df):
    # Bug: df['amount'] contains nulls causing division by zero
    # Ultra AI Guided Fix Applied:
    clean_df = df.filter(df['amount'].isNotNull())
    return clean_df.withColumn('avg_spend', clean_df['total'] / clean_df['count'])`,
    demoHeadline: 'Repository & Workspace Debugging',
    demoBullets: [
      'Scans full project files for unhandled nulls and type mismatches.',
      'Provides 1-click guided patch applications with undo history.',
      'Generates automated inline docstrings and schema lineage notes.'
    ]
  },
  {
    id: 'etl-pipeline-generation',
    title: 'ETL Pipeline Generation',
    description:
      'Create ETL pipelines from functional requirements with paired SQL and Python artifacts, review generated workflows, and prepare them before execution.',
    icon: Workflow,
    color: 'from-emerald-600 to-teal-600',
    accentBorder: 'border-slate-200 hover:border-emerald-500',
    badge: 'Automated ETL',
    codeSnippet: `# Generated ETL Pipeline: Snowflake -> Postgres Staging
from candor import PipelineEngine, SQLTask

pipeline = PipelineEngine(name="daily_sales_sync")
pipeline.add_task(SQLTask(source="snowflake_prod", script="extract_sales.sql"))
pipeline.add_task(SQLTask(target="postgres_dw", script="load_fact_sales.sql"))
pipeline.run_1shot()`,
    demoHeadline: 'Paired SQL & Python Artifact Generator',
    demoBullets: [
      'Converts natural language requirements into complete ETL scripts.',
      'Pairs Python extractors with optimized target SQL loaders.',
      'Scaffolds directory layout, YAML configs, and validation tests.'
    ]
  },
  {
    id: 'direct-sql-workspace',
    title: 'Direct SQL Workspace',
    description:
      'Execute SQL queries against connected databases directly from the IDE, view results inside chat, and display supported query outputs with inline visualizations.',
    icon: Terminal,
    color: 'from-amber-600 to-orange-600',
    accentBorder: 'border-slate-200 hover:border-amber-500',
    badge: 'Interactive Queries',
    codeSnippet: `-- Direct SQL Execution in Candor IDE
SELECT region, COUNT(*) as active_users, SUM(revenue) as total_rev
FROM analytics.user_activity
GROUP BY region ORDER BY total_rev DESC;

/* Results (3 Rows Returned in 14ms):
+-----------+--------------+-----------+
| Region    | Active_Users | Total_Rev |
+-----------+--------------+-----------+
| US-East   | 142,500      | $4.2M     |
| EU-West   | 98,200       | $2.8M     |
| AP-South  | 64,100       | $1.9M     |
+-----------+--------------+-----------+ */`,
    demoHeadline: 'In-IDE SQL Query Console & Visualizer',
    demoBullets: [
      'Run SQL queries directly against active live connections.',
      'Inspect tabular outputs, data profiling metrics, and charts.',
      'Export query results to CSV, Parquet, or JSON with 1 click.'
    ]
  },
  {
    id: 'database-connection-manager',
    title: 'Database Connection Manager',
    description:
      'Create, test, manage, and securely store multiple database connections through a centralized connection manager with encrypted credentials.',
    icon: ShieldCheck,
    color: 'from-blue-700 to-indigo-700',
    accentBorder: 'border-slate-200 hover:border-blue-600',
    badge: 'Encrypted Security',
    codeSnippet: `/* Connection Manager Configuration */
{
  "connection_name": "Snowflake_Prod_DW",
  "driver": "snowflake-jdbc",
  "host": "xy12345.us-east-1.snowflakecomputing.com",
  "database": "ANALYTICS_PROD",
  "warehouse": "COMPUTE_WH",
  "auth_type": "OAuth2 / Encrypted RSA",
  "status": "Connected (TLS 1.3 Encryption Active)"
}`,
    demoHeadline: 'Centralized & Encrypted Credential Vault',
    demoBullets: [
      'AES-256 encrypted local credential storage.',
      'Supports SSL/TLS certificates, SSO, OAuth, and IAM roles.',
      'Test connectivity and inspect latency across all data targets.'
    ]
  },
  {
    id: 'multi-database-support',
    title: 'Multi-Database Support',
    description:
      'Connect to SQL Server, PostgreSQL, Snowflake, Oracle, MySQL, Databricks, SQLite, and other supported databases from one development environment.',
    icon: Layers,
    color: 'from-pink-600 to-rose-600',
    accentBorder: 'border-slate-200 hover:border-pink-500',
    badge: 'Cross-Platform',
    codeSnippet: `-- Cross-Database Dialect Compatibility
-- SQL Server (T-SQL) -> Snowflake SQL -> PostgreSQL Dialect
SELECT TOP 10 ISNULL(user_name, 'N/A') FROM legacy_sql_server.users;
-- Candor Auto-Translates to Target Postgres:
SELECT COALESCE(user_name, 'N/A') FROM postgres_dw.users LIMIT 10;`,
    demoHeadline: 'Multi-Database Dialect Transpiler',
    demoBullets: [
      'Single IDE to query Microsoft SQL Server, Snowflake, Postgres, Oracle.',
      'Auto-translates T-SQL, PL/SQL, MySQL, and Snowflake SQL syntax.',
      'Switch active working database connection instantly.'
    ]
  },
  {
    id: 'workflow-automation',
    title: 'Workflow Automation',
    description:
      'Generate workflow scripts and automation diagrams directly from natural language requirements to simplify repetitive engineering tasks.',
    icon: GitBranch,
    color: 'from-violet-600 to-purple-700',
    accentBorder: 'border-slate-200 hover:border-violet-500',
    badge: 'Natural Language',
    codeSnippet: `# Generated Airflow DAG Definition
from airflow import DAG
from airflow.operators.python import PythonOperator
from datetime import datetime

with DAG("daily_etl_workflow", start_date=datetime(2026, 1, 1), schedule_interval="@daily") as dag:
    extract_task = PythonOperator(task_id="extract_raw", python_callable=extract)
    transform_task = PythonOperator(task_id="transform_dw", python_callable=transform)
    extract_task >> transform_task`,
    demoHeadline: 'Natural Language to Workflow DAGs',
    demoBullets: [
      'Generate Apache Airflow, Prefect, and Dagster pipeline definitions.',
      'Visual diagram previews of task dependencies and retry logic.',
      'Automates cron scheduling and notification webhooks.'
    ]
  },
  {
    id: 'sql-review-diff',
    title: 'SQL Review & Diff Management',
    description:
      'Review AI-generated SQL changes before applying them using Keep, Undo, and SQL-aware diff tools designed to preserve ETL structures.',
    icon: FileCode2,
    color: 'from-sky-600 to-blue-700',
    accentBorder: 'border-slate-200 hover:border-sky-500',
    badge: 'Safety Controls',
    codeSnippet: `--- ORIGINAL SQL ---
+++ CANDOR SUGGESTED SQL +++
- SELECT * FROM orders;
+ SELECT order_id, customer_id, amount, created_at
+ FROM orders
+ WHERE is_active = TRUE;
[ KEEP ]  [ UNDO ]  [ APPLY ALL ]`,
    demoHeadline: 'SQL-Aware Diff & Review Tool',
    demoBullets: [
      'Side-by-side color coded diff comparison before writing to disk.',
      'Granular Keep & Undo controls per query block.',
      'Prevents accidental schema drop or destructive UPDATE statements.'
    ]
  },
  {
    id: 'ai-chat-modes',
    title: 'AI Chat Modes',
    description:
      'Switch between Auto, Ultra AI, ETL, Workflow, Debug, Direct SQL, Template, and General modes based on the task you\'re working on.',
    icon: MessageSquare,
    color: 'from-teal-600 to-emerald-700',
    accentBorder: 'border-slate-200 hover:border-teal-500',
    badge: '8 Specialized Modes',
    codeSnippet: `/* Candor SI Mode Controller */
Active Mode: [ ETL Mode ⚡ ]
Capabilities:
- Schema Introspection: ACTIVE
- Code Artifact Output: SQL + Python
- Query Execution: DIRECT ON TARGET
Modes Available: Auto, Ultra AI, ETL, Workflow, Debug, Direct SQL, Template, General`,
    demoHeadline: '8 Task-Optimized AI Modes',
    demoBullets: [
      'Switch modes dynamically depending on whether you are debugging or writing ETL.',
      'ETL Mode outputs production scripts with schema validation.',
      'Direct SQL Mode executes queries live with instant table output.'
    ]
  },
  {
    id: 'project-session-management',
    title: 'Project & Session Management',
    description:
      'Save conversations, export chat history, manage long-running projects, and maintain development context across IDE sessions.',
    icon: FolderKanban,
    color: 'from-indigo-600 to-blue-800',
    accentBorder: 'border-slate-200 hover:border-indigo-500',
    badge: 'Persistent Context',
    codeSnippet: `/* Candor Project Workspace State */
Project: "Enterprise_Finance_Migration"
Sessions Saved: 14 Active Trajectories
Metadata Cache: 42 Tables Synced
Export Formats: JSON, Markdown, HTML Report, Python Package`,
    demoHeadline: 'Multi-Project Context & History Preservation',
    demoBullets: [
      'Full history persistence across IDE restarts and team handoffs.',
      'Export prompt sessions and lineage logs to Markdown or JSON.',
      'Isolated session memory per database connection.'
    ]
  },
  {
    id: 'nl-sql-data-questions',
    title: 'Natural-Language ETL & NL→SQL',
    description:
      'Ask complex data questions in plain English. Candor introspects live database catalogs, resolves tables and foreign-key join relationships, and generates validated SQL with interactive result tables and SVG charts.',
    icon: Search,
    color: 'from-blue-600 to-indigo-600',
    accentBorder: 'border-slate-200 hover:border-blue-500',
    badge: 'NL→SQL Engine',
    codeSnippet: `-- Natural-Language Data Query
-- Prompt: "Show our top 10 enterprise customers by quarterly recurring revenue"
SELECT 
  c.customer_name,
  c.enterprise_tier,
  SUM(s.mrr_amount * 3) AS quarterly_revenue,
  s.sla_status
FROM snowflake_dw.finance.subscriptions s
JOIN snowflake_dw.crm.customers c ON s.customer_id = c.customer_id
WHERE s.is_active = TRUE AND c.enterprise_tier = 'Tier-1'
GROUP BY 1, 2, 4
ORDER BY quarterly_revenue DESC
LIMIT 10;
/* Formatted Result: 10 rows with interactive SVG distribution chart */`,
    demoHeadline: 'Conversational Data Assistant & Query Visualizer',
    demoBullets: [
      'Converts plain-English questions into dialect-optimized SQL in real time.',
      'Discovers foreign-key relationships and resolves joins across up to 300 tables.',
      'Renders formatted result tables and responsive SVG chart visualizations.',
      'Read-only safety guard prevents accidental execution of raw English prompts.'
    ]
  },
  {
    id: 'autonomous-agent-platform',
    title: 'Autonomous Agent Platform',
    description:
      'Delegate multi-step data engineering workflows to Candor\'s built-in agent platform (/agent) equipped with 120+ extensible tools, deep code search, impact analysis, and persistent memory.',
    icon: Bot,
    color: 'from-purple-600 to-pink-600',
    accentBorder: 'border-slate-200 hover:border-purple-500',
    badge: '120+ Agent Tools',
    codeSnippet: `# Candor Agent Command: /agent run task "Migrate legacy MySQL pipeline to Databricks"
[Agent Daemon] Ingesting project lineage & database catalogs... (32 tables)
[Agent Tool] AST_ANALYZER -> Detected 14 type mappings & 3 sequence conversions
[Agent Tool] CODE_PATCHER -> Generated delta_pipeline.py & schema_sync.sql
[Agent Tool] VERIFY_HARNESS -> Running automated dry-run validation...
[Agent Result] Status: PASSED ✓ (0 regressions, ready for diff review)`,
    demoHeadline: 'Multi-Step Autonomous Engineering Agents',
    demoBullets: [
      'Execute multi-step tasks autonomously with /agent directly inside the IDE.',
      'Extensible catalog of 120+ tools: filesystem, git, AST patches, terminal, and MCP.',
      'Agent memory store retains project architecture context across team handoffs.',
      'Built-in impact analysis, policy review queue, and safe execution boundaries.'
    ]
  },
  {
    id: 'byok-multi-model-hub',
    title: 'AI Models & BYOK Hub',
    description:
      'Bring your own API keys for leading frontier models (OpenAI, Anthropic Claude, Google Gemini, AWS Bedrock, Azure, DeepSeek, Groq), or run completely private and air-gapped with local LLMs.',
    icon: Zap,
    color: 'from-amber-600 to-red-600',
    accentBorder: 'border-slate-200 hover:border-amber-500',
    badge: '12+ Providers & Local',
    codeSnippet: `/* Candor SI Engine - Model Configuration */
{
  "active_engine": "BYOK_Custom_Provider",
  "selected_model": "claude-3-5-sonnet-20241022",
  "supported_providers": [
    "OpenAI (GPT-4o, o1)", "Anthropic (Claude 3.5 Sonnet)",
    "Google Gemini (1.5 Pro/Flash)", "AWS Bedrock", "Azure OpenAI",
    "Groq", "DeepSeek", "Mistral", "xAI (Grok)", "Together AI"
  ],
  "local_airgap": "Ollama (Llama 3.1, Qwen2.5-Coder) - 100% Offline Active",
  "fallback_routing": "Automated Bedrock / Secondary Failover"
}`,
    demoHeadline: 'Multi-Model Freedom & Zero-Telemetry Privacy',
    demoBullets: [
      'Bring your own API keys for OpenAI, Anthropic, Gemini, AWS Bedrock, and Azure.',
      'Run 100% offline and air-gapped using local models via Ollama integration.',
      'Native support for advanced reasoning models (Qwen, Nemotron, gpt-oss).',
      'Automated multi-model failover and enterprise credential management.'
    ]
  },
  {
    id: 'whisper-voice-dictation',
    title: 'Desktop Voice Dictation',
    description:
      'Scaffold pipelines, generate SQL, and request code explanations hands-free with high-precision Whisper neural voice dictation integrated directly into the Candor IDE chat toolbar.',
    icon: Mic,
    color: 'from-emerald-600 to-cyan-600',
    accentBorder: 'border-slate-200 hover:border-emerald-500',
    badge: 'Whisper Dictation',
    codeSnippet: `# Candor Voice Dictation Engine (Whisper Neural Model)
# User speaks: "Create an incremental ETL job to sync Postgres billing events into Snowflake"
>>> [Voice Ingest]: Audio stream captured (16kHz PCM)
>>> [Transcription]: "Create an incremental ETL job to sync Postgres billing events into Snowflake"
>>> [Candor Action]: Routing to ETL Mode -> Introspecting postgres.public.billing_events...
>>> [Generated]: sync_billing_events.sql + run_etl_pipeline.py`,
    demoHeadline: 'Hands-Free Voice-to-Pipeline Engineering',
    demoBullets: [
      'One-click microphone input in the IDE chat bar for prompt-driven development.',
      'High-fidelity speech recognition powered by Whisper neural models.',
      'Understands technical data jargon, dialect terms, and database table names.',
      'Accelerates prompt drafting and complex code troubleshooting without typing.'
    ]
  },
  {
    id: 'automated-verification-loop',
    title: 'Automated Verification Loop',
    description:
      'Catch and fix pipeline issues automatically. Candor listens to terminal errors, executes background test harnesses, and applies verified deterministic AST patches before human review.',
    icon: RefreshCw,
    color: 'from-teal-600 to-blue-700',
    accentBorder: 'border-slate-200 hover:border-teal-500',
    badge: 'Self-Healing Loops',
    codeSnippet: `-- Candor Automated Verification Loop --
[Step 1] Execute ETL script: run_etl_script.py (ColumnMismatchError detected)
[Step 2] Terminal error captured: Column "tax_exempt_flag" not found in Target DDL.
[Step 3] Introspecting catalog -> Resolved target column: "is_tax_exempt (BOOLEAN)".
[Step 4] Applying AST deterministic patch to SQL & Python artifacts.
[Step 5] Re-running verification harness... [PASSED ✓ 0 errors, 12,400 rows processed]`,
    demoHeadline: 'Terminal Error Capture & Closed-Loop Code Repair',
    demoBullets: [
      'Listens to terminal outputs and automatically captures execution stack traces.',
      'Closed-loop verification runs background test harnesses to validate fixes.',
      'Deterministic AST patch engine corrects column mappings and type mismatches.',
      'Ensures zero syntax regressions and clean execution before diff review.'
    ]
  },
  {
    id: 'multiplatform-desktop-builds',
    title: 'Cross-Platform Desktop IDE',
    description:
      'Built for maximum developer performance with native releases for Windows 10/11, macOS (Apple Silicon M-Series & Intel DMG), and Linux (.deb & portable tarballs).',
    icon: Monitor,
    color: 'from-slate-700 to-indigo-900',
    accentBorder: 'border-slate-200 hover:border-indigo-600',
    badge: 'Win · Mac · Linux',
    codeSnippet: `/* Multi-Platform Desktop Architecture */
{
  "version": "1.0.5",
  "platforms": {
    "windows": { "arch": ["x64 (.exe installer)", "x64 (.zip portable)"] },
    "macos":   { "arch": ["Apple Silicon (ARM64 M1-M4)", "Intel Mac (x64)"] },
    "linux":   { "arch": ["Debian / Ubuntu (.deb)", "Tarball Archive (.tar.gz)"] }
  },
  "features": ["Native OS notifications", "Fast startup", "Auto-update manifest"]
}`,
    demoHeadline: 'Universal Native Desktop Experience',
    demoBullets: [
      'Native installers and portable archives available for Windows 10 & 11.',
      'Dedicated Apple Silicon (ARM64) and Intel (x64) DMG bundles for macOS.',
      '64-bit Debian/Ubuntu package (.deb) and portable archives for Linux distributions.',
      'Automated updates and seamless session persistence across all operating systems.'
    ]
  }
];

// Why Teams Choose Candor
const WHY_CHOOSE_CANDOR = [
  { text: 'Schema-aware AI assistance', desc: 'Validates code against active database DDL before execution.' },
  { text: 'AI-powered SQL and Python generation', desc: 'Creates paired production artifacts from natural language requirements.' },
  { text: 'Enterprise ETL automation', desc: 'Builds end-to-end extraction, transformation, and load workflows.' },
  { text: 'Repository-aware debugging', desc: 'Understands full codebase lineage and guides complex troubleshooting.' },
  { text: 'Multi-database connectivity', desc: 'Unified development workspace across 11+ major enterprise connectors.' },
  { text: 'Secure credential management', desc: 'AES-256 encrypted local credential storage with OAuth & TLS.' },
  { text: 'SQL review before applying changes', desc: 'Keep and Undo diff tools safeguard against destructive statements.' },
  { text: 'Built-in workflow generation', desc: 'Generates Airflow/Prefect DAGs and automation diagrams instantly.' },
  { text: 'Direct SQL execution', desc: 'Run queries and view inline tabular/chart outputs inside the chat console.' },
  { text: 'Unified AI development workspace', desc: 'Combines chat modes, session history, and live IDE controls in one window.' },
  { text: 'Natural-language NL→SQL querying', desc: 'Ask complex analytical questions in plain English with instant SQL generation and SVG charts.' },
  { text: 'Autonomous multi-tool Agent Platform', desc: 'Execute multi-step tasks with impact analysis, code search, memory store, and 120+ tools.' },
  { text: 'Bring Your Own Key (BYOK) freedom', desc: 'Connect OpenAI, Anthropic, Gemini, Bedrock, DeepSeek, or run 100% private with local Ollama.' },
  { text: 'Hands-free Whisper voice dictation', desc: 'Draft prompts, describe pipeline logic, and troubleshoot code with high-speed neural voice input.' }
];

// Supported Databases
const SUPPORTED_DATABASES = [
  { name: 'Microsoft SQL Server', category: 'Enterprise DB', icon: '🗄️', badge: 'T-SQL Native' },
  { name: 'PostgreSQL', category: 'Relational', icon: '🐘', badge: 'JSONB Support' },
  { name: 'Snowflake', category: 'Cloud Warehouse', icon: '❄️', badge: 'Zero-Copy Clone' },
  { name: 'Oracle', category: 'Enterprise DB', icon: '⚙️', badge: 'PL/SQL' },
  { name: 'MySQL', category: 'Relational', icon: '🐬', badge: 'InnoDB' },
  { name: 'MariaDB', category: 'Relational', icon: '🦭', badge: 'ColumnStore' },
  { name: 'SQLite', category: 'Embedded', icon: '📦', badge: 'Local File' },
  { name: 'DuckDB', category: 'Analytical', icon: '🦆', badge: 'In-Memory OLAP' },
  { name: 'Databricks', category: 'Lakehouse', icon: '🧱', badge: 'Delta Lake' },
  { name: 'Vertica', category: 'Analytics', icon: '⚡', badge: 'Columnar' },
  { name: 'SAP HANA', category: 'In-Memory', icon: '💼', badge: 'Enterprise OLAP' }
];

// Built for Every Data Team
const TARGET_ROLES = [
  {
    role: 'Data Engineers',
    desc: 'Accelerate ETL creation, generate Python pipelines, and optimize SQL transforms with native schema intelligence.',
    icon: Workflow,
    tag: 'Pipeline Builders'
  },
  {
    role: 'Data Architects',
    desc: 'Design resilient data models, standardize prompt skills, and structure multi-database warehouse architectures.',
    icon: Layers,
    tag: 'System Designers'
  },
  {
    role: 'ETL Developers',
    desc: 'Generate paired SQL and Python code instantly from functional requirements with built-in diff review.',
    icon: Code2,
    tag: 'Automation Leads'
  },
  {
    role: 'Analytics Engineers',
    desc: 'Transform raw warehouse datasets into clean, business-ready analytical models with context-aware AI.',
    icon: Database,
    tag: 'dbt & SQL Experts'
  },
  {
    role: 'Database Administrators',
    desc: 'Debug query bottlenecks, manage multi-db connections securely, and review schema changes safely.',
    icon: ShieldCheck,
    tag: 'DB Governance'
  },
  {
    role: 'BI Developers',
    desc: 'Bridge raw database structures directly into reporting-ready star schemas and aggregated SQL views.',
    icon: Terminal,
    tag: 'Reporting & Analytics'
  },
  {
    role: 'Data Platform Teams',
    desc: 'Standardize enterprise AI workflows, enforce data quality standards, and manage workspace credentials.',
    icon: Boxes,
    tag: 'Platform Ops'
  }
];

// FAQs
const FAQ_ITEMS = [
  {
    question: 'What is Candor?',
    answer:
      'Candor is an AI-assisted data engineering platform that combines schema-aware AI, ETL generation, SQL execution, workflow automation, and intelligent debugging within a desktop IDE.'
  },
  {
    question: 'What databases does Candor support?',
    answer:
      'Candor supports Microsoft SQL Server, PostgreSQL, Snowflake, Oracle, MySQL, Databricks, SQLite, MariaDB, DuckDB, Vertica, SAP HANA, and other supported connectors.'
  },
  {
    question: 'What is Ultra AI?',
    answer:
      'Ultra AI is Candor\'s repository-aware AI assistant that helps analyze projects, fix SQL and Python code, generate documentation, and support ETL workflows.'
  },
  {
    question: 'Can I execute SQL directly from Candor?',
    answer:
      'Yes. Direct SQL mode allows you to run SQL queries against active database connections and view results directly within the IDE.'
  },
  {
    question: 'Does Candor support ETL generation?',
    answer:
      'Yes. Candor generates ETL workflows with paired SQL and Python artifacts, allowing you to review generated content before execution.'
  },
  {
    question: 'How does Candor help with SQL reviews?',
    answer:
      'Candor includes SQL-aware diff tools with Keep and Undo functionality, allowing developers to review AI-generated changes before applying them.'
  },
  {
    question: 'Is database connection information stored securely?',
    answer:
      'Candor encrypts stored database credentials and supports multiple authentication methods depending on the database platform.'
  },
  {
    question: 'Can I ask data questions in natural English (NL→SQL)?',
    answer:
      'Yes. Candor includes native NL→SQL capabilities. You can describe your analytical inquiry in plain English, and Candor will introspect your connected database catalog, resolve foreign-key relationships, generate dialect-optimized SQL, and render formatted tabular results alongside interactive SVG chart visualizers.'
  },
  {
    question: 'What is the Candor Agent Platform?',
    answer:
      'The Candor Agent Platform (/agent) enables autonomous multi-step execution of complex data tasks. It features an extensible catalog of 120+ tools including filesystem inspection, git operations, AST code patching, verification loops, and MCP (Model Context Protocol) integration, backed by long-term project memory.'
  },
  {
    question: 'Can I bring my own AI model API keys (BYOK)?',
    answer:
      'Yes. Candor SI Engine provides flexible Bring-Your-Own-Key (BYOK) support for OpenAI, Anthropic (Claude 3.5 Sonnet), Google Gemini, AWS Bedrock, Azure OpenAI, Groq, DeepSeek, Mistral, and other providers, as well as 100% offline local models via Ollama for air-gapped security.'
  },
  {
    question: 'Does Candor support Voice Dictation?',
    answer:
      'Yes. Candor includes integrated Whisper neural voice dictation. Developers can click the microphone icon in the IDE chat to dictate prompts, describe pipeline logic, and request code explanations hands-free with high technical recognition accuracy.'
  },
  {
    question: 'What desktop operating systems are supported?',
    answer:
      'Candor Desktop IDE is available for Windows (Windows 10 & 11 via installer and portable zip), macOS (Apple Silicon ARM64 for M1/M2/M3/M4 chips and Intel x64 via DMG), and Linux (Debian/Ubuntu 64-bit .deb packages and portable archives).'
  }
];

export default function FeaturesPage({ onOpenContact, onOpenDownload, onOpenLogin }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // States
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [faqSearchQuery, setFaqSearchQuery] = useState('');
  const [selectedFeatureModal, setSelectedFeatureModal] = useState(null);
  const [activeDbCategory, setActiveDbCategory] = useState('All');
  const [activeHeroTab, setActiveHeroTab] = useState('sql');

  // Video Modal State
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [isDemoFullSize, setIsDemoFullSize] = useState(false);
  const demoVideoRef = useRef(null);

  const toggleFullScreenVideo = () => {
    setIsDemoFullSize((prev) => !prev);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showDemoModal) {
        setShowDemoModal(false);
        setIsDemoFullSize(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDemoModal]);

  // Trigger video play when modal opens
  useEffect(() => {
    if (showDemoModal && demoVideoRef.current) {
      demoVideoRef.current.currentTime = 0;
      const playPromise = demoVideoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log('Video autoplay deferred by browser policy:', error);
        });
      }
    }
  }, [showDemoModal]);

  // Handlers
  const handlePrimaryCta = () => {
    if (onOpenDownload) {
      onOpenDownload();
    } else if (onOpenLogin) {
      onOpenLogin();
    } else {
      navigate('/login');
    }
  };

  const handleSecondaryCta = () => {
    const el = document.getElementById('feature-highlights');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/features');
    }
  };

  const handleContactSales = () => {
    if (onOpenContact) {
      onOpenContact(
        'Candor IDE Enterprise Sales Inquiry',
        'Hello, I would like to explore Candor IDE for our data engineering team, including enterprise licenses, multi-database support, and deployment options.'
      );
    } else {
      navigate('/about');
    }
  };

  // Filters
  const filteredFaqs = FAQ_ITEMS.filter(
    (item) =>
      item.question.toLowerCase().includes(faqSearchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(faqSearchQuery.toLowerCase())
  );

  const filteredDbs = SUPPORTED_DATABASES.filter((db) => {
    if (activeDbCategory === 'All') return true;
    if (activeDbCategory === 'Cloud') return db.category.includes('Cloud') || db.category.includes('Lakehouse');
    if (activeDbCategory === 'Relational') return db.category.includes('Relational') || db.category.includes('Enterprise');
    if (activeDbCategory === 'Analytics') return db.category.includes('Analytics') || db.category.includes('Analytical');
    return true;
  });

  return (
    <div className="animate-fade-in bg-white min-h-screen font-sans text-slate-900 antialiased selection:bg-blue-600 selection:text-white">

      {/* Marquee Animations */}
      <style>{`
        @keyframes infiniteLogoMarquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-logo-marquee {
          display: flex;
          width: max-content;
          animation: infiniteLogoMarquee 28s linear infinite;
        }
        .animate-logo-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* ========================================================================= */}
      {/* 1. HERO SECTION (MATCHING HOMEPAGE PATTERNS & BUTTON STYLES)              */}
      {/* ========================================================================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12" aria-label="Hero Features Showcase">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          
          {/* Left Hero Column */}
          <div className="lg:col-span-6 flex flex-col pt-1">
            
            {/* TACTICAL BADGE (MATCHING HOMEPAGE BADGE) */}
            <div className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-blue-50 text-blue-900 border border-blue-200 w-fit mb-4">
              AI-POWERED DATA ENGINEERING PLATFORM
            </div>
            
            {/* HERO HEADLINE (H1 - MATCHING HOMEPAGE FONT COLOR & LEADING) */}
            <h1 className="text-3xl sm:text-4xl lg:text-[38px] font-extrabold text-slate-900 leading-[1.25] tracking-tight mb-5">
              Build, Debug, and Modernize Data Pipelines with{' '}
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Schema-Aware AI
              </span>
            </h1>
            
            {/* SUBTITLE COPY (MATCHING HOMEPAGE COPY FORMAT) */}
            <p className="text-sm text-slate-700 font-medium leading-[1.75] mb-6">
              Candor combines AI-assisted code generation, schema-aware SQL transformation, ETL automation, and intelligent debugging into a unified data engineering workspace. Connect your databases, generate SQL and Python, validate against live schemas, and accelerate development with confidence.
            </p>

            {/* BULLET POINTS (SHORT HEADINGS ONLY) */}
            <div className="flex flex-col gap-2.5 mb-6 text-xs sm:text-sm font-extrabold text-slate-900">
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 rounded-full bg-blue-700 flex items-center justify-center shrink-0" aria-hidden="true">
                  <Check className="w-3 h-3 text-white stroke-[3]" />
                </div>
                <span>Active Schema-Aware AI Assistance</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 rounded-full bg-blue-700 flex items-center justify-center shrink-0" aria-hidden="true">
                  <Check className="w-3 h-3 text-white stroke-[3]" />
                </div>
                <span>Automated SQL & Python ETL Generation</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 rounded-full bg-blue-700 flex items-center justify-center shrink-0" aria-hidden="true">
                  <Check className="w-3 h-3 text-white stroke-[3]" />
                </div>
                <span>Repository-Aware Debugging & Diff Tools</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 rounded-full bg-blue-700 flex items-center justify-center shrink-0" aria-hidden="true">
                  <Check className="w-3 h-3 text-white stroke-[3]" />
                </div>
                <span>Native Support for 11+ Connectors</span>
              </div>
            </div>

            {/* ACTION CTAs (MATCHING HOMEPAGE BUTTON STYLES & SIZES) */}
            <div className="flex flex-nowrap items-center gap-2 mb-6 overflow-x-auto pb-1" role="group" aria-label="Action CTAs">
              
              {/* 1. Try Sandbox */}
              <button 
                onClick={handlePrimaryCta}
                aria-label="Try Candor Sandbox in Browser"
                className="px-3.5 py-2 bg-emerald-700 hover:bg-emerald-600 focus-visible:ring-2 focus-visible:ring-emerald-700 text-white text-[11px] font-extrabold rounded-lg shadow-md flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all transform hover:-translate-y-0.5"
              >
                <Zap className="w-3.5 h-3.5 fill-current" aria-hidden="true" />
                <span>Try Sandbox</span>
              </button>

              {/* 2. Download IDE */}
              <button 
                onClick={handlePrimaryCta}
                aria-label="Download Candor Desktop IDE Application"
                className="px-3.5 py-2 bg-blue-700 hover:bg-blue-600 focus-visible:ring-2 focus-visible:ring-blue-700 text-white text-[11px] font-extrabold rounded-lg shadow-md flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all transform hover:-translate-y-0.5"
              >
                <Download className="w-3.5 h-3.5" aria-hidden="true" />
                <span>Download IDE</span>
              </button>

              {/* 3. Open Web IDE */}
              <button 
                onClick={() => window.open('/web', '_blank')}
                aria-label="Open Candor Web IDE Application"
                className="px-3.5 py-2 bg-white hover:bg-slate-50 border border-slate-400 focus-visible:ring-2 focus-visible:ring-blue-700 text-slate-900 text-[11px] font-extrabold rounded-lg shadow-sm flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all transform hover:-translate-y-0.5"
              >
                <Globe className="w-3.5 h-3.5 text-blue-700" aria-hidden="true" />
                <span>Open Web IDE</span>
              </button>

              {/* 4. Watch Demo */}
              <button 
                onClick={() => setShowDemoModal(true)}
                aria-label="Watch Candor IDE 1-Shot Demo Video"
                className="px-3.5 py-2 bg-indigo-700 hover:bg-indigo-600 focus-visible:ring-2 focus-visible:ring-indigo-700 text-white text-[11px] font-extrabold rounded-lg shadow-md flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all transform hover:-translate-y-0.5"
              >
                <span className="text-sm">🎬</span>
                <span>Watch Demo</span>
              </button>

            </div>

          </div>

          {/* Right Hero Preview Window (HOMEPAGE CODE EDITOR WIDGET) */}
          <div className="lg:col-span-6 lg:pt-12">
            <div className="relative bg-[#080D1A] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden font-sans text-xs text-slate-300 transform hover:scale-[1.01] transition-all duration-300">
              
              {/* Window Header */}
              <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-rose-500/80" />
                    <span className="w-3 h-3 rounded-full bg-amber-500/80" />
                    <span className="w-3 h-3 rounded-full bg-emerald-500/80" />
                  </div>
                  <span className="ml-2 font-bold text-slate-200 text-xs font-mono">
                    <span className="text-blue-400 font-bold">Candor</span> IDE — <span className="text-slate-300">orders_pipeline.py</span>
                  </span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-950 text-emerald-400 border border-emerald-800 text-[10px] font-bold flex items-center gap-1 font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping inline-block" />
                  Live Sync ✓
                </span>
              </div>

              {/* Tab Controls */}
              <div className="bg-slate-900/90 border-b border-slate-800 px-4 py-2 flex items-center justify-between text-[11px] font-mono">
                <div className="flex items-center gap-2 text-slate-400">
                  <Database className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-slate-200 font-bold">snowflake_prod.public</span>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setActiveHeroTab('sql')}
                    className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
                      activeHeroTab === 'sql'
                        ? 'bg-blue-700 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    SQL Transform
                  </button>
                  <button
                    onClick={() => setActiveHeroTab('python')}
                    className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
                      activeHeroTab === 'python'
                        ? 'bg-blue-700 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Python ETL
                  </button>
                  <button
                    onClick={() => setActiveHeroTab('schema')}
                    className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
                      activeHeroTab === 'schema'
                        ? 'bg-blue-700 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Schema Sync
                  </button>
                </div>
              </div>

              {/* Code Window */}
              <div className="p-4 space-y-3 min-h-[290px] font-mono text-[11px] bg-[#080D1A]">
                {activeHeroTab === 'sql' && (
                  <pre className="text-[10px] leading-relaxed text-slate-300 font-mono bg-slate-900/80 p-3.5 rounded-xl border border-slate-800 overflow-x-auto">
                    <span className="text-slate-500">-- 1-Shot Schema-Aware Transformation</span>{'\n'}
                    <span className="text-purple-400">CREATE OR REPLACE TABLE</span> snowflake_prod.analytics.fact_daily_sales <span className="text-purple-400">AS</span>{'\n'}
                    <span className="text-purple-400">SELECT</span>{'\n'}
                    {'  '}o.order_id,{'\n'}
                    {'  '}c.customer_name,{'\n'}
                    {'  '}<span className="text-blue-400">SUM</span>(o.total_amount) <span className="text-purple-400">AS</span> net_revenue,{'\n'}
                    {'  '}<span className="text-blue-400">COUNT</span>(i.item_id) <span className="text-purple-400">AS</span> total_items{'\n'}
                    <span className="text-purple-400">FROM</span> snowflake_prod.public.orders o{'\n'}
                    <span className="text-purple-400">JOIN</span> snowflake_prod.public.customers c <span className="text-purple-400">ON</span> o.customer_id = c.customer_id{'\n'}
                    <span className="text-purple-400">LEFT JOIN</span> snowflake_prod.public.items i <span className="text-purple-400">ON</span> o.order_id = i.order_id{'\n'}
                    <span className="text-purple-400">GROUP BY</span> 1, 2;
                  </pre>
                )}

                {activeHeroTab === 'python' && (
                  <pre className="text-[10px] leading-relaxed text-slate-300 font-mono bg-slate-900/80 p-3.5 rounded-xl border border-slate-800 overflow-x-auto">
                    <span className="text-slate-500"># Paired Python Pipeline Execution</span>{'\n'}
                    <span className="text-purple-400">from</span> candor <span className="text-purple-400">import</span> PipelineEngine, Connector{'\n'}
                    {'\n'}
                    <span className="text-blue-400">db</span> = Connector.get(<span className="text-amber-300">"snowflake_prod"</span>){'\n'}
                    <span className="text-blue-400">engine</span> = PipelineEngine(connection=db){'\n'}
                    <span className="text-blue-400">result</span> = engine.execute_script(<span className="text-emerald-400">"orders_etl.sql"</span>){'\n'}
                    print(f<span className="text-amber-300">"Rows loaded: {'{'}result.rows_inserted{'}'} in {'{'}result.execution_ms{'}'}ms"</span>)
                  </pre>
                )}

                {activeHeroTab === 'schema' && (
                  <div className="space-y-2 text-[11px]">
                    <div className="text-slate-400 font-bold text-[9px] uppercase tracking-wider flex justify-between border-b border-slate-800 pb-1">
                      <span>LIVE METADATA INTROSPECTION</span>
                      <span className="text-emerald-400">SCHEMA MATCH 100% ✓</span>
                    </div>
                    <div className="bg-slate-900 p-2.5 rounded-lg border border-slate-800 space-y-1 text-[10px]">
                      <div className="text-blue-400 font-bold">snowflake_prod.public.orders</div>
                      <div className="text-slate-400 space-y-0.5 pl-2">
                        <div className="flex justify-between"><span>order_id</span><span className="text-amber-300">BIGINT PK</span></div>
                        <div className="flex justify-between"><span>customer_id</span><span className="text-amber-300">BIGINT FK</span></div>
                        <div className="flex justify-between"><span>total_amount</span><span className="text-amber-300">DECIMAL(12,2)</span></div>
                        <div className="flex justify-between"><span>order_date</span><span className="text-amber-300">TIMESTAMP</span></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status Bar */}
                <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-[10px]">
                  <span className="text-emerald-400 font-bold flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    Zero Syntax Hallucinations
                  </span>
                  <span className="text-slate-400 font-mono">Status: Ready</span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 2. INFINITE MOVABLE LOGO MARQUEE CAROUSEL                                 */}
      {/* ========================================================================= */}
      <section className="py-8 bg-white border-b border-slate-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <div className="text-xs font-extrabold text-slate-500 uppercase tracking-widest mb-6">
            NATIVELY COMPATIBLE WITH 15+ ENTERPRISE DATA ENGINES
          </div>
          
          {/* Edge Mask Fades */}
          <div className="absolute top-10 left-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute top-10 right-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          {/* Marquee Container */}
          <div className="overflow-hidden">
            <div className="animate-logo-marquee flex items-center gap-4">
              {[...DB_LOGOS, ...DB_LOGOS].map((db, idx) => (
                <div
                  key={idx}
                  className="px-5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 text-xs font-bold flex items-center gap-2 hover:border-blue-500 hover:text-blue-700 hover:scale-105 transition-all shadow-2xs shrink-0 cursor-pointer"
                >
                  <span>{db.icon}</span>
                  <span>{db.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* 3. FEATURE HIGHLIGHTS & 16 FEATURE GRID CARDS                            */}
      {/* ========================================================================= */}
      <section id="feature-highlights" className="bg-white py-16 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Section Header (Homepage Design Typography) */}
          <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
            <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-blue-50 text-blue-900 border border-blue-200">
              FEATURE HIGHLIGHTS
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
              Everything You Need for Modern Data Engineering
            </h2>
            <p className="text-sm sm:text-base text-slate-700 font-medium leading-[1.75]">
              From SQL generation, autonomous agent workflows, and NL→SQL query visualization to BYOK model flexibility, voice dictation, and multi-database connectivity, Candor brings together the essential tools for designing, validating, and maintaining enterprise data workflows.
            </p>
          </div>

          {/* 16 Feature Grid Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURE_GRID.map((feat) => {
              const IconComp = feat.icon;
              return (
                <div
                  key={feat.id}
                  onClick={() => setSelectedFeatureModal(feat)}
                  className={`bg-slate-50 border ${feat.accentBorder} rounded-2xl p-6 shadow-sm hover:bg-white hover:shadow-md transition-all duration-300 flex flex-col justify-between cursor-pointer group relative overflow-hidden`}
                >
                  <div className="space-y-4">
                    
                    {/* Icon + Badge */}
                    <div className="flex items-center justify-between">
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-r ${feat.color} text-white flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform`}>
                        <IconComp className="w-5.5 h-5.5" />
                      </div>
                      <span className="text-[10px] font-extrabold text-slate-600 uppercase tracking-wider bg-white px-3 py-1 rounded-full border border-slate-200 shadow-2xs">
                        {feat.badge}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-extrabold text-slate-900 tracking-tight group-hover:text-blue-700 transition-colors">
                      {feat.title}
                    </h3>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed">
                      {feat.description}
                    </p>

                  </div>

                  {/* Card Bottom CTA Link */}
                  <div className="pt-5 border-t border-slate-200/80 flex items-center justify-between text-xs font-extrabold text-blue-700">
                    <span className="group-hover:underline">View Code & Details</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 4. WHY TEAMS CHOOSE CANDOR                                               */}
      {/* ========================================================================= */}
      <section className="bg-white py-16 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
            <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-blue-50 text-blue-900 border border-blue-200">
              ENTERPRISE VALUE
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              Why Teams Choose Candor
            </h2>
            <p className="text-sm sm:text-base text-slate-700 font-medium leading-[1.75]">
              Designed specifically for technical data teams who require precision, live schema introspection, and rapid iteration.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {WHY_CHOOSE_CANDOR.map((item, idx) => {
              return (
                <div
                  key={idx}
                  className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 hover:bg-white hover:shadow-md hover:border-blue-300 transition-all duration-300 flex items-start gap-3.5"
                >
                  <div className="w-5 h-5 rounded-full bg-blue-700 flex items-center justify-center shrink-0 mt-0.5" aria-hidden="true">
                    <Check className="w-3.5 h-3.5 text-white stroke-[3]" />
                  </div>

                  <div>
                    <h3 className="font-extrabold text-sm text-slate-900 mb-1">
                      {item.text}
                    </h3>
                    <p className="text-xs text-slate-700 font-medium leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 5. SUPPORTED DATABASES                                                   */}
      {/* ========================================================================= */}
      <section className="bg-white py-16 border-b border-slate-100 text-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-blue-50 text-blue-900 border border-blue-200">
              UNIVERSAL CONNECTORS
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              Supported Databases
            </h2>
            <p className="text-sm sm:text-base text-slate-700 font-medium leading-[1.75]">
              Candor supports connections across a broad range of enterprise databases, including:
            </p>
          </div>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {['All', 'Cloud', 'Relational', 'Analytics'].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveDbCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-extrabold transition-all ${
                  activeDbCategory === cat
                    ? 'bg-blue-700 text-white shadow-md'
                    : 'bg-white text-slate-700 hover:text-slate-900 border border-slate-300'
                }`}
              >
                {cat === 'All' ? 'All Databases' : `${cat} DBs`}
              </button>
            ))}
          </div>

          {/* Database Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filteredDbs.map((db, idx) => (
              <div
                key={idx}
                className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 text-center hover:bg-white hover:border-blue-500 hover:shadow-md hover:scale-105 transition-all duration-200 group flex flex-col justify-between"
              >
                <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">{db.icon}</div>
                <div>
                  <div className="font-extrabold text-xs sm:text-sm text-slate-900">{db.name}</div>
                  <div className="text-[10px] font-semibold text-slate-500 mt-1">{db.category}</div>
                </div>
                <div className="mt-3 pt-2 border-t border-slate-200 text-[9px] font-mono text-blue-700 font-bold">
                  {db.badge}
                </div>
              </div>
            ))}

            <div className="bg-blue-50/70 border border-blue-200 rounded-2xl p-4 text-center flex flex-col justify-center items-center">
              <span className="text-2xl mb-1">🌐</span>
              <span className="font-extrabold text-xs text-blue-900">Custom JDBC/ODBC</span>
              <span className="text-[10px] text-slate-600 mt-1">Generic Driver</span>
            </div>
          </div>

          {/* Outro Note */}
          <div className="mt-10 text-center text-xs font-extrabold text-slate-700 bg-slate-50 max-w-xl mx-auto py-3.5 px-6 rounded-full border border-slate-200 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>Additional connection options are available through the Connection Manager.</span>
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 6. BUILT FOR EVERY DATA TEAM                                             */}
      {/* ========================================================================= */}
      <section className="bg-white py-16 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-14 space-y-4">
            <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-blue-50 text-blue-900 border border-blue-200">
              ROLE PROFILES
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              Built for Every Data Team
            </h2>
            <p className="text-sm sm:text-base text-slate-700 font-medium leading-[1.75]">
              Candor supports a wide range of technical roles, including:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TARGET_ROLES.map((item, idx) => {
              const IconComp = item.icon;
              return (
                <div
                  key={idx}
                  className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 hover:bg-white hover:shadow-md hover:border-blue-400 transition-all duration-300 flex flex-col justify-between space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-800 border border-blue-200 flex items-center justify-center font-bold text-lg">
                      <IconComp className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-extrabold text-blue-900 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
                      {item.tag}
                    </span>
                  </div>

                  <div>
                    <h3 className="font-extrabold text-lg text-slate-900 mb-2">{item.role}</h3>
                    <p className="text-xs sm:text-sm text-slate-700 font-medium leading-relaxed">{item.desc}</p>
                  </div>

                  <div className="pt-3 border-t border-slate-200/80 flex items-center gap-2 text-xs font-bold text-slate-800">
                    <div className="w-4 h-4 rounded-full bg-blue-700 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-white stroke-[3]" />
                    </div>
                    <span>Schema-Aware AI Acceleration</span>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 7. FREQUENTLY ASKED QUESTIONS                                             */}
      {/* ========================================================================= */}
      <section className="bg-white py-16 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-10 space-y-4">
            <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-wider uppercase bg-blue-50 text-blue-900 border border-blue-200">
              KNOWLEDGE BASE
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-sm sm:text-base text-slate-700 font-medium">
              Search or click any question to review technical details about Candor IDE.
            </p>
          </div>

          {/* Search Input */}
          <div className="relative max-w-xl mx-auto mb-8">
            <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search questions (e.g. databases, SQL, Ultra AI, security)..."
              value={faqSearchQuery}
              onChange={(e) => setFaqSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm font-medium focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20 shadow-xs"
            />
            {faqSearchQuery && (
              <button
                onClick={() => setFaqSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                Clear
              </button>
            )}
          </div>

          {/* Accordion */}
          <div className="space-y-4">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div
                    key={idx}
                    className="bg-slate-50 border border-slate-200/80 rounded-2xl overflow-hidden shadow-xs hover:border-blue-400 transition-all duration-200"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(isOpen ? -1 : idx)}
                      className="w-full px-6 py-5 text-left font-extrabold text-slate-900 text-base sm:text-lg flex items-center justify-between gap-4 hover:bg-white transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <HelpCircle className="w-5 h-5 text-blue-700 shrink-0" />
                        <span>{faq.question}</span>
                      </div>
                      <ChevronDown
                        className={`w-5 h-5 text-slate-500 shrink-0 transition-transform duration-300 ${
                          isOpen ? 'rotate-180 text-blue-700' : ''
                        }`}
                      />
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-6 pt-1 text-slate-700 text-sm sm:text-base font-medium leading-[1.75] border-t border-slate-200/80 bg-white">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-10 bg-slate-50 rounded-2xl border border-slate-200">
                <p className="text-slate-500 font-bold text-sm">No matching questions found for "{faqSearchQuery}".</p>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* ========================================================================= */}
      {/* 8. FINAL EXECUTIVE CTA BANNER (EXACT HOMEPAGE FOOTER BANNER STYLING)     */}
      {/* ========================================================================= */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
            
            {/* Background Glow */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

            {/* Left Column */}
            <div className="relative z-10 flex items-center gap-4 text-left">
              <div className="w-11 h-11 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-400 flex items-center justify-center font-bold text-xl shrink-0 shadow-sm">
                ⚡
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-extrabold text-white tracking-tight">
                  Build Better Data Pipelines with Schema-Aware AI
                </h2>
                <p className="text-slate-400 text-xs mt-0.5 font-normal">
                  Download Candor IDE and explore AI-assisted SQL generation, ETL automation, repository-aware debugging, and multi-database development.
                </p>
              </div>
            </div>

            {/* Right Buttons Column (4 HOMEPAGE ACTION BUTTONS) */}
            <div className="relative z-10 flex flex-wrap items-center gap-2.5 shrink-0 w-full md:w-auto justify-end" role="group" aria-label="Final CTAs">
              
              {/* Try Sandbox */}
              <button 
                onClick={handlePrimaryCta}
                aria-label="Try Candor Sandbox in Browser"
                className="px-3.5 py-2 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-extrabold rounded-lg shadow-md flex items-center gap-1.5 shrink-0 transition-all transform hover:-translate-y-0.5"
              >
                <Zap className="w-3.5 h-3.5 fill-current" aria-hidden="true" />
                <span>Try Sandbox</span>
              </button>

              {/* Download IDE */}
              <button 
                onClick={handlePrimaryCta}
                aria-label="Download Candor Desktop IDE"
                className="px-3.5 py-2 bg-blue-700 hover:bg-blue-600 text-white text-xs font-extrabold rounded-lg shadow-md flex items-center gap-1.5 shrink-0 transition-all transform hover:-translate-y-0.5"
              >
                <Download className="w-3.5 h-3.5" aria-hidden="true" />
                <span>Download IDE</span>
              </button>

              {/* Open Web IDE */}
              <button 
                onClick={() => window.open('/web', '_blank')}
                aria-label="Open Candor Web IDE Application"
                className="px-3.5 py-2 bg-white hover:bg-slate-50 border border-slate-400 text-slate-900 text-xs font-extrabold rounded-lg shadow-sm flex items-center gap-1.5 shrink-0 transition-all transform hover:-translate-y-0.5"
              >
                <Globe className="w-3.5 h-3.5 text-blue-700" aria-hidden="true" />
                <span>Open Web IDE</span>
              </button>

              {/* Watch Demo */}
              <button 
                onClick={() => setShowDemoModal(true)}
                aria-label="Watch Candor IDE 1-Shot Demo Video"
                className="px-3.5 py-2 bg-indigo-700 hover:bg-indigo-600 text-white text-xs font-extrabold rounded-lg shadow-md flex items-center gap-1.5 shrink-0 transition-all transform hover:-translate-y-0.5"
              >
                <span className="text-sm">🎬</span>
                <span>Watch Demo</span>
              </button>

            </div>

          </div>
        </div>
      </section>

      {/* ========================================================================= */}
      {/* WATCH DEMO VIDEO MODAL DIALOG                                              */}
      {/* ========================================================================= */}
      {showDemoModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-sm animate-fade-in"
          onClick={() => {
            setShowDemoModal(false);
            setIsDemoFullSize(false);
          }}
          role="presentation"
        >
          <div 
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-modal-title"
            className={`relative bg-[#0f0f0f] border border-neutral-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 ${
              isDemoFullSize 
                ? 'w-full max-w-[95vw] max-h-[92vh] h-[90vh]' 
                : 'w-full max-w-3xl'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Header */}
            <div className="bg-[#181818] px-4 py-2.5 border-b border-neutral-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex items-center gap-1 bg-red-600 text-white font-black text-[11px] px-2 py-0.5 rounded tracking-tighter shrink-0">
                  <span>▶</span>
                  <span>YouTube</span>
                </div>
                <h2 id="demo-modal-title" className="font-sans text-xs sm:text-sm font-semibold text-neutral-200 truncate">
                  Candor CDP Platform — 1-Shot Schema Awareness & Pipeline Demo
                </h2>
              </div>

              <div className="flex items-center gap-1.5 ml-2 shrink-0">
                <button
                  onClick={toggleFullScreenVideo}
                  className="px-2.5 py-1 rounded-md bg-neutral-800 hover:bg-neutral-700 text-neutral-300 hover:text-white flex items-center gap-1 transition-all text-xs font-medium border border-neutral-700 shadow-sm"
                  title={isDemoFullSize ? "Compact Size" : "Full Size Video"}
                >
                  <span className="text-sm">{isDemoFullSize ? '⤡' : '⛶'}</span>
                  <span className="hidden sm:inline text-[11px] font-semibold">{isDemoFullSize ? 'Compact' : 'Full Size'}</span>
                </button>

                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    setIsDemoFullSize(false);
                  }}
                  className="w-7 h-7 rounded-full bg-neutral-800 hover:bg-neutral-700 text-neutral-400 hover:text-white flex items-center justify-center transition-colors text-sm font-bold"
                >
                  <span>✕</span>
                </button>
              </div>
            </div>

            {/* Video Player */}
            <div className={`relative bg-black overflow-hidden group ${isDemoFullSize ? 'flex-1 h-full' : 'aspect-video'}`}>
              <video
                ref={demoVideoRef}
                autoPlay
                controls
                playsInline
                preload="auto"
                className="w-full h-full object-contain"
              >
                <source src="/assets/Candor_xDE_User_Guide.webm" type="video/webm" />
                <source src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4" type="video/mp4" />
                Your browser does not support HTML5 video playback.
              </video>
            </div>

            {/* Footer */}
            <div className="bg-[#181818] px-4 py-3 border-t border-neutral-800 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-xs shadow-md">
                  C
                </div>
                <div>
                  <div className="font-bold text-white flex items-center gap-1 text-xs">
                    <span>Candor SI</span>
                    <span className="text-blue-400 text-[10px]">✓</span>
                  </div>
                  <div className="text-[10px] text-neutral-400">12.8k subscribers</div>
                </div>
                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    handlePrimaryCta();
                  }}
                  className="ml-2 px-3.5 py-1.5 rounded-full bg-red-600 hover:bg-red-500 text-white font-bold text-xs transition-all shadow-sm flex items-center gap-1"
                >
                  <span>⚡</span> Try Sandbox
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    handlePrimaryCta();
                  }}
                  className="px-4 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-600 text-white font-extrabold text-xs transition-all shadow-md"
                >
                  Download IDE
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* INTERACTIVE FEATURE DETAIL MODAL DIALOG                                   */}
      {/* ========================================================================= */}
      {selectedFeatureModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
          <div className="relative w-full max-w-3xl bg-white border border-slate-200 rounded-3xl shadow-2xl overflow-hidden font-sans">
            
            {/* Modal Header */}
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-r ${selectedFeatureModal.color} text-white flex items-center justify-center shadow-md`}>
                  {React.createElement(selectedFeatureModal.icon, { className: 'w-5 h-5' })}
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-base leading-tight">
                    {selectedFeatureModal.title}
                  </h3>
                  <span className="text-slate-500 text-xs">{selectedFeatureModal.demoHeadline}</span>
                </div>
              </div>

              <button
                onClick={() => setSelectedFeatureModal(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-rose-600 text-slate-500 hover:text-white flex items-center justify-center font-bold text-sm transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5">
              <p className="text-slate-700 text-sm font-medium leading-[1.75]">
                {selectedFeatureModal.description}
              </p>

              {/* Code Snippet Box */}
              <div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center justify-between font-mono">
                  <span>LIVE IDE EXECUTION ARTIFACT</span>
                  <span className="text-blue-700 font-bold">{selectedFeatureModal.badge}</span>
                </div>
                <pre className="bg-[#080D1A] p-4 rounded-xl border border-slate-800 text-xs text-slate-200 font-mono leading-relaxed overflow-x-auto max-h-60">
                  {selectedFeatureModal.codeSnippet}
                </pre>
              </div>

              {/* Technical Bullets */}
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2">
                <div className="text-xs font-extrabold text-slate-900 uppercase tracking-wider">Key Capabilities:</div>
                <ul className="space-y-1.5 text-xs text-slate-700 font-medium">
                  {selectedFeatureModal.demoBullets.map((b, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 rounded-full bg-blue-700 flex items-center justify-center shrink-0">
                        <Check className="w-2.5 h-2.5 text-white stroke-[3]" />
                      </div>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center justify-between text-xs">
              <span className="text-slate-500 font-medium">Included in Candor IDE Standard & Enterprise</span>
              <button
                onClick={() => {
                  setSelectedFeatureModal(null);
                  handlePrimaryCta();
                }}
                className="px-5 py-2.5 rounded-xl bg-blue-700 hover:bg-blue-600 text-white font-extrabold text-xs transition-all shadow-md"
              >
                Try in Candor IDE Now
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
